import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import {
    calculateItemDeliveryStatus,
    calculateItemInvoicedStatus,
    calculateTotalAmountInvoiced,
    calculateTotalQuantityReceived,
} from "@finway-group/shared/lib/utils/purchaseOrder.util"
import { Col, Collapse, Row } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ExpandableListItemIcon } from "~Components/icons/ExpandableListItemIcon"
import { useCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { getTaxLabel, insertIf } from "~Shared/utils/helper.utils"
import { ILineItem } from "../types/LineItems.interfaces"
import { PurchaseOrderDeliveryStatusTag } from "./PurchaseOrderDeliveryStatusTag"
import { PurchaseOrderInvoicedStatusTag } from "./PurchaseOrderInvoicedStatusTag"

const defaultValStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
}

export const LineItemCardReadOnly = ({ item, idx, currency }: { item: ILineItem; idx: number; currency: CurrencyEnum }) => {
    const { t } = useTranslation()

    const [isExpanded, setIsExpanded] = useState(false)

    const costCenters = useCostCenters()
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()

    const costCenterName = useMemo(() => {
        const costCenter = costCenters.find((cc) => cc._id === item.costCenter)
        return costCenter?.name
    }, [costCenters, item.costCenter])

    const costCenter2Name = useMemo(() => {
        const costCenter2 = costCenters2.find((cc) => cc._id === item.costCenter2)
        return costCenter2?.name
    }, [costCenters2, item.costCenter2])

    const expenseAccountName = useMemo(() => {
        const expenseAccount = expenseAccounts.find((ea) => ea._id === item.expenseAccount)
        return expenseAccount?.accountName
    }, [expenseAccounts, item.expenseAccount])

    const thirdRowColumns = [
        {
            title: t("purchaseOrders:details.vat"),
            span: 3,
            value: getTaxLabel({ taxRate: item?.taxRate?.taxRate }),
            valStyle: {},
        },
        {
            title: t("purchaseOrders:details.totalVatAmount"),
            span: 5,
            value: formatCurrencyNumber({ value: item.totalTaxPrice, currency }),
            valStyle: {},
        },
        {
            title: t("purchaseOrders:details.costCenter"),
            span: 4,
            valStyle: defaultValStyle,
            value: costCenterName,
            classNames: "truncate",
        },
        ...insertIf(!!item.costCenter2, {
            title: t("purchaseOrders:details.costCenter2"),
            span: 4,
            valStyle: defaultValStyle,
            value: costCenter2Name,
            classNames: "truncate",
        }),
        ...insertIf(!!item.expenseAccount, {
            title: t("purchaseOrders:details.expenseAccount"),
            span: 8,
            valStyle: defaultValStyle,
            value: expenseAccountName,
            classNames: "truncate",
        }),
    ]

    const fourthRowColumns = [
        ...insertIf(!!item.note, {
            title: t("purchaseOrders:drawer.note"),
            span: 24,
            value: item.note,
        }),
    ]

    const fourthRow = (
        <div>
            <Row className="font-bold text-xs">
                {fourthRowColumns.map((column, index) => (
                    <Col span={column.span} key={index}>
                        {column.title}
                    </Col>
                ))}
            </Row>
            <Row className="text-xs">
                {fourthRowColumns.map((column, index) => (
                    <Col span={column.span} key={index}>
                        {column.value}
                    </Col>
                ))}
            </Row>
        </div>
    )

    const thirdRow = (
        <div className="flex flex-col w-full gap-8 justify-between mb-12">
            {/* Header row */}
            <Row className="font-bold text-xs">
                {thirdRowColumns.map((column, index) => (
                    <Col span={column.span} key={index}>
                        {column.title}
                    </Col>
                ))}
            </Row>
            {/* Value row */}
            <Row className="text-xs truncate">
                {thirdRowColumns.map((column, index) => (
                    <Col span={column.span} key={index} className={column.classNames}>
                        {column.value}
                    </Col>
                ))}
            </Row>
        </div>
    )

    const secondRow = (
        <div className="flex flex-col w-full gap-8 justify-between mb-12">
            <Row className="font-bold text-xs">
                <Col span={3}>{t("purchaseOrders:details.quantity")}</Col>
                <Col span={5}>{t("purchaseOrders:details.quantityReceived")}</Col>
                <Col span={4}>{t("purchaseOrders:details.totalNet")}</Col>
                <Col span={4}>{t("purchaseOrders:details.totalGross")}</Col>
                <Col span={8}>{t("purchaseOrders:details.totalAmountInvoiced")}</Col>
            </Row>
            <Row className="text-xs">
                <Col span={3}>{item.quantity}</Col>
                <Col span={5}>{calculateTotalQuantityReceived(item)}</Col>
                <Col span={4}>{formatCurrencyNumber({ value: item.totalNetPrice, currency })}</Col>
                <Col span={4}>{formatCurrencyNumber({ value: item.totalGrossPrice, currency })}</Col>
                <Col span={8}>{formatCurrencyNumber({ value: calculateTotalAmountInvoiced(item), currency })}</Col>
            </Row>
        </div>
    )

    const firstRow = (
        <>
            <Row gutter={12} align="middle" className="mb-12">
                <Col>
                    <div>
                        <ExpandableListItemIcon expanded={isExpanded} className="block" />
                    </div>
                </Col>
                <Col>
                    <div className="w-40 font-bold bg-gray-100 rounded-full text-center p-2 flex items-center justify-center">{String(idx + 1)}</div>
                </Col>
                {item.materialCode ? (
                    <Col
                        className={`w-100 font-medium bg-gray-100 rounded-full overflow-x-auto`}
                        style={{
                            scrollbarWidth: "none",
                        }}
                    >
                        <span className="px-8 py-4 whitespace-nowrap inline-block min-w-full text-center">{item.materialCode}</span>
                    </Col>
                ) : null}
                <Col flex="auto" className="truncate">
                    <div className="truncate max-w-300">
                        {item.link ? (
                            <a
                                href={item.link ? (item.link.startsWith("http://") || item.link.startsWith("https://") ? item.link : `https://${item.link}`) : ""}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span>{item.name}</span>
                            </a>
                        ) : (
                            <span>{item.name}</span>
                        )}
                    </div>
                </Col>
                <Col>
                    <PurchaseOrderInvoicedStatusTag status={calculateItemInvoicedStatus(item)} />
                </Col>
                <Col>
                    <PurchaseOrderDeliveryStatusTag status={calculateItemDeliveryStatus(item)} />
                </Col>
            </Row>
            {secondRow}
        </>
    )

    return (
        <Collapse ghost bordered onChange={() => setIsExpanded(!isExpanded)}>
            <Collapse.Panel key={idx} header={firstRow} showArrow={false}>
                {thirdRow}
                {fourthRow}
            </Collapse.Panel>
        </Collapse>
    )
}
